import { useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import CreateModal from "@/pages/Jobs/CreateModal";
import { LegCreateModal } from "@/pages/Jobs/Legs/LegCreate/Modal";
import TableLayout from "../TableLayout/NewTableLayout";
import {
  useAssignResourcesMutation,
  useGetAssignedJobsQuery,
  useGetUnassignedJobsQuery,
} from "@/redux/services/schedule/scheduleApi";
import getUnassignedJobsTableConfig from "./unassigned-table.configs";
import { useConfig, useFetchAll, useForm } from "@/hooks";
import { AssignResourcesModal } from "@/modals";
import type { AppType } from "@/types/config";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import TableSearch from "@/shared/Table/TableSearch";
import TablePagination from "@/shared/Table/Pagination";

interface DispatchLayoutProps {
  resources?: any[];
  title?: string;
  subtitle: any;
  icon?: React.ReactElement;
  selectedDate: Date;
  onDataUpdate: (data: any) => void;
  onResourceAssignment?: (
    eventId: string,
    resourceIds: string[],
  ) => Promise<any>;
  isUpdating?: boolean;
  onUnassignedJobDrop?: (
    jobId: string,
    targetResourceId: string,
    targetTime?: Date,
  ) => void;
  onModalOpen?: () => void;
  showDispatchLayoutToggle?: boolean;
  onDispatchLayoutToggle?: () => void;
  isDispatchLayoutVisible?: boolean;
  [x: string]: any;
}

const DispatchLayout: React.FC<DispatchLayoutProps> = ({
  onDataUpdate,
  isUpdating = false,
  onUnassignedJobDrop,
  ...props
}) => {
  const { appType } = useConfig();
  const [activeTab, setActiveTab] = useState<"assigned" | "unassigned">(
    "unassigned",
  );
  const [isAssignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any>(null);

  const [addModalVisible, setAddModalVisible] = useState(false);

  // Leg modal and form state
  const [legModalVisible, setLegModalVisible] = useState(false);
  const legForm = useForm({});

  // Assigned jobs/legs tab (lazy-loaded only when selected)
  const {
    pagination: {
      pageIndex: assignedPageIndex,
      pageSize: assignedPageSize,
      setPagination: setAssignedPagination,
    },
    search: {
      handleGlobalSearch: handleAssignedGlobalSearch,
      clearSearch: clearAssignedSearch,
      search: assignedSearch,
    },
    queryResult: {
      data: assignedJobsResult,
      isLoading: isLoadingAssigned,
      isError: assignedError,
      refetch: refetchAssigned,
    },
  } = useFetchAll({
    queryHook: useGetAssignedJobsQuery,
    defaultPageSize: 15,
    options: {
      skip: activeTab !== "assigned",
    },
  });

  // Unassigned jobs/legs tab (lazy-loaded only when clicked)
  const {
    pagination: {
      pageIndex: unassignedPageIndex,
      pageSize: unassignedPageSize,
      setPagination: setUnassignedPagination,
    },
    search: {
      handleGlobalSearch: handleUnassignedGlobalSearch,
      clearSearch: clearUnassignedSearch,
      search: unassignedSearch,
    },
    queryResult: {
      data: unassignedJobsResult,
      isLoading: isLoadingUnassigned,
      isError: unassignedError,
      refetch: refetchUnassigned,
    },
  } = useFetchAll({
    queryHook: useGetUnassignedJobsQuery,
    defaultPageSize: 20,
    options: {
      skip: activeTab !== "unassigned",
    },
  });

  // console.log("unassignedJobsResult", unassignedJobsResult);

  const [assignResources, { isLoading: isAssigning }] =
    useAssignResourcesMutation();

  const extractRows = useCallback((data: any) => {
    if (!data) return [];
    const rawResult = data as any;
    const result = rawResult?.data || rawResult;

    const extractedData =
      result.unassignedLegs ||
      result.assignedLegs ||
      result.unassignedJobs ||
      result.assignedJobs ||
      result.data?.unassignedLegs ||
      result.data?.assignedLegs ||
      result.data?.unassignedJobs ||
      result.data?.assignedJobs ||
      result.data?.docs ||
      result.docs ||
      result.data?.schedule ||
      result.data?.jobs ||
      result.data?.events ||
      result.jobs ||
      result.events ||
      (Array.isArray(result.data) ? result.data : null) ||
      (Array.isArray(result) ? result : null);

    return Array.isArray(extractedData) ? extractedData : [];
  }, []);

  const assignedRows = useMemo(
    () => extractRows(assignedJobsResult),
    [assignedJobsResult, extractRows],
  );
  const unassignedRows = useMemo(
    () => extractRows(unassignedJobsResult),
    [unassignedJobsResult, extractRows],
  );
  const activeRows = activeTab === "assigned" ? assignedRows : unassignedRows;

  const assignedPaginationData = useMemo(() => {
    const rawResult = assignedJobsResult as any;
    return rawResult?.data || rawResult || null;
  }, [assignedJobsResult]);
  const unassignedPaginationData = useMemo(() => {
    const rawResult = unassignedJobsResult as any;
    return rawResult?.data || rawResult || null;
  }, [unassignedJobsResult]);
  const activePaginationData =
    activeTab === "assigned" ? assignedPaginationData : unassignedPaginationData;

  // console.log("unassignedJobs", unassignedJobs);
  const handleOpenAssignModal = (job: any) => {
    // console.log("job", job);
    setSelectedJob(job);
    setAssignModalOpen(true);
    // Notify parent that a modal is opening
    if (props.onModalOpen) {
      props.onModalOpen();
    }
  };

  const handleCloseAssignModal = () => {
    setSelectedJob(null);
    setAssignModalOpen(false);
  };

  const handleAssignResources = async (
    resourceIds: string[],
    options?: {
      assignToAllUnassignedLegs?: boolean;
      assignToAll?: boolean;
      appointmentDate?: Date | null;
      otherLegsAssignments?: {
        [key: string]: { driver: string | null; appointmentDate: Date | null };
      };
    },
  ) => {
    if (!selectedJob) return;

    try {
      // Create optimized payload for network efficiency
      const payload = {
        id: selectedJob._id,
        resourceIds, // Only the essential driver IDs
        // Only include boolean flags if they're true (reduce payload size)
        ...(options?.assignToAllUnassignedLegs && {
          assignToAllUnassignedLegs: true,
        }),
        ...(options?.assignToAll && { assignToAll: true }),
        // Only include appointment date if it's set
        ...(options?.appointmentDate && {
          appointmentDate: options.appointmentDate,
        }),
        // Only include other legs assignments if there are any changes
        ...(options?.otherLegsAssignments &&
          Object.keys(options.otherLegsAssignments).length > 0 && {
            otherLegsAssignments: options.otherLegsAssignments,
          }),
      };

      await assignResources(payload).unwrap();

      const successMessage = options?.assignToAll
        ? "Driver assigned to all legs in job successfully!"
        : options?.assignToAllUnassignedLegs
          ? "Driver assigned to all unassigned legs in job successfully!"
          : "Resources assigned successfully!";

      toast.success(successMessage);
      handleCloseAssignModal();
      // Trigger data update and refetch
      onDataUpdate?.({
        type: "assignment",
        eventId: selectedJob.id || selectedJob._id,
        patch: {
          assignedResources: resourceIds,
        },
      });
      if (activeTab === "unassigned") {
        refetchUnassigned?.();
      }
      refetchAssigned?.();
    } catch (error) {
      toast.error("Failed to assign resources.");
      console.error(error);
    }
  };

  // Leg modal handlers
  const handleOpenLegModal = (legData: any) => {
    // console.log("Opening leg modal with data:", legData);

    // Extract route information from nested structure
    const route = legData?.route || {};
    const origin = route?.origin || null;
    const destination = route?.destination || null;
    const defaultMileage = route?.defaultMileage ?? route?.mileage ?? null;
    const explicitCustomMileage =
      route?.customMileage ??
      route?.billedMiles ??
      legData?.customMileage ??
      legData?.billedMiles ??
      null;
    const baseTotalMiles = legData?.totalMiles ?? route?.mileage ?? 0;
    const shouldUseCustomMileage =
      (explicitCustomMileage != null && explicitCustomMileage > 0) ||
      (defaultMileage != null &&
        baseTotalMiles != null &&
        baseTotalMiles > 0 &&
        baseTotalMiles !== defaultMileage);
    const resolvedCustomMileage = shouldUseCustomMileage
      ? explicitCustomMileage ?? baseTotalMiles
      : null;
    const resolvedTotalMiles = shouldUseCustomMileage
      ? resolvedCustomMileage
      : baseTotalMiles;

    // Check if we have custom values that should be preserved
    const hasCustomBaseCharge = legData.baseCharge > 0;
    const hasCustomFuelSurcharge = legData.fuelSurcharge > 0;
    const hasCustomDropCharge = legData.dropCharge > 0;
    const hasCustomOtherCharges = legData.otherCharges > 0;
    const hasCustomMileage = shouldUseCustomMileage;

    // Pre-populate the form with the leg data, extracting route info to the root level
    legForm.setFormInit({
      ...legData,
      job: legData.jobId, // Pass the job object
      edit: true, // Mark as edit mode

      // Extract route information to root level for the form
      origin: origin,
      destination: destination,
      totalMiles: resolvedTotalMiles || 0,
      defaultMileage: defaultMileage ?? resolvedTotalMiles ?? 0,
      customMileage: resolvedCustomMileage,
      billedMiles: route?.billedMiles ?? resolvedCustomMileage,

      // Set custom flags to preserve existing charge values
      useCustomBaseCharge: hasCustomBaseCharge,
      useCustomFuelSurcharge: hasCustomFuelSurcharge,
      useCustomDropCharge: hasCustomDropCharge,
      useCustomOtherCharges: hasCustomOtherCharges,
      useCustomMileage: hasCustomMileage,

      // Extract driver information
      driver: legData.driver,

      // Map other nested properties that the form expects
      client: legData.client,
    });
    setLegModalVisible(true);
  };

  const handleCloseLegModal = () => {
    setLegModalVisible(false);
    legForm.clearForm("");
  };

  // Handle drag end for unassigned jobs
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !active.data.current) return;

    const draggedItem = active.data.current;
    const dropTarget = over.data.current;

    // Check if dragging an unassigned job to a resource row
    if (
      draggedItem.type === "unassigned-job" &&
      dropTarget?.type === "resource-row"
    ) {
      const job = draggedItem.job;
      const targetResource = dropTarget.resource;

      if (job && targetResource) {
        // Call the parent's drop handler or assign resources directly
        if (onUnassignedJobDrop) {
          onUnassignedJobDrop(job._id, targetResource.id);
        } else {
          // Fallback to direct assignment
          handleAssignResources([targetResource.id]);
          setSelectedJob(job);
        }
      }
    }
  };

  const tableMeta = {
    assignResources: handleOpenAssignModal,
    openLegModal: handleOpenLegModal,
  };

  const columns = useMemo(
    () =>
      appType
        ? getUnassignedJobsTableConfig(appType as AppType, {
            mode: activeTab,
            actionLabel: activeTab === "assigned" ? "Reassign" : "Assign",
          }).COLUMN_DEFINITIONS
        : [],
    [activeTab, appType],
  );

  const { DraggableTableRow } = useMemo(
    () =>
      appType
        ? getUnassignedJobsTableConfig(appType as AppType, {
            mode: activeTab,
          })
        : { DraggableTableRow: null },
    [activeTab, appType],
  );

  // Error handling
  const activeError = activeTab === "assigned" ? assignedError : unassignedError;
  const activeLoading =
    (activeTab === "assigned" ? isLoadingAssigned : isLoadingUnassigned) ||
    isUpdating;
  const activePageIndex =
    activeTab === "assigned" ? assignedPageIndex : unassignedPageIndex;
  const activePageSize =
    activeTab === "assigned" ? assignedPageSize : unassignedPageSize;
  const activeSetPagination =
    activeTab === "assigned" ? setAssignedPagination : setUnassignedPagination;
  const activeSearch = activeTab === "assigned" ? assignedSearch : unassignedSearch;
  const activeHandleGlobalSearch =
    activeTab === "assigned"
      ? handleAssignedGlobalSearch
      : handleUnassignedGlobalSearch;
  const activeClearSearch =
    activeTab === "assigned" ? clearAssignedSearch : clearUnassignedSearch;
  const activeRefetch =
    activeTab === "assigned" ? refetchAssigned : refetchUnassigned;
  const activePageSizeOptions =
    activeTab === "assigned" ? [10, 20, 50] : [10, 20, 50, 100];

  const renderTableHeader = useCallback(
    (headerProps: any) => {
      return (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-2 md:px-3 md:py-2 border-b border-gray-100">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-1 shadow-sm shrink-0">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab("unassigned")}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === "unassigned"
                      ? "bg-white text-primary-700 shadow-md border-l-2 border-primary-500"
                      : "text-gray-600 hover:bg-white/80 hover:text-gray-800"
                  }`}
                >
                  Unassigned Jobs
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("assigned")}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === "assigned"
                      ? "bg-white text-primary-700 shadow-md border-l-2 border-primary-500"
                      : "text-gray-600 hover:bg-white/80 hover:text-gray-800"
                  }`}
                >
                  Assigned Legs
                </button>
              </div>
            </div>

            <div className="min-w-[220px] max-w-xl w-full">
              <TableSearch
                table={headerProps.table}
                globalFilter={headerProps.globalFilter}
                setGlobalFilter={headerProps.setGlobalFilter}
              />
            </div>
          </div>

          <div className="shrink-0 flex items-center gap-2">
            {headerProps.pagination && (
              <TablePagination
                state={headerProps.state}
                pagination={headerProps.pagination}
                {...headerProps}
              />
            )}
            {headerProps.renderActions && headerProps.renderActions(headerProps)}
          </div>
        </div>
      );
    },
    [activeTab],
  );

  if (activeError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600 mb-4">
            Error loading {activeTab === "assigned" ? "assigned" : "unassigned"} jobs
          </p>
          <button
            onClick={() => activeRefetch?.()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <div className="flex flex-col h-full min-h-0 gap-4">
        <div className="flex-1 min-h-0">
          <TableLayout
            data={activeRows}
            columns={columns.length ? columns : undefined}
            pagination={activePaginationData}
            pageIndex={activePageIndex}
            pageSize={activePageSize}
            onPaginationChange={activeSetPagination}
            title={activeTab === "assigned" ? "Assigned Legs" : "Unassigned Jobs"}
            id={activeTab === "assigned" ? "assigned-legs" : "unassigned-jobs"}
            loading={activeLoading}
            pageSizeOptions={activePageSizeOptions}
            mainAction={{
              onClick: () => {
                // Close event modal if open
                if (props.onModalOpen) {
                  props.onModalOpen();
                }
                setAddModalVisible(true);
              },
            }}
            compactHeader={true}
            hideCheckbox={true}
            search={activeSearch}
            fullTextSearch={activeHandleGlobalSearch}
            globalSearchFilters={["loadNumber", "orderNumber"]}
            handleGlobalSearch={activeHandleGlobalSearch}
            clearSearch={activeClearSearch}
            meta={tableMeta}
            customRowComponent={
              activeTab === "unassigned" ? DraggableTableRow || undefined : undefined
            }
            // actions={null}
            createPath={null}
            handleEdit={null}
            handleMove={null}
            handleDelete={null}
            editPath={null}
            noEdit={true}
            noDelete={true}
            onUpdate={null}
            fullSelectedItem={null}
            enableSorting={true}
            hideActions={false}
            // hideMainAction={false}
            handleDownloadPDF={null}
            actions={null}
            hideMainAction={activeTab === "assigned"}
            stickyHeaders={false}
            renderHeader={renderTableHeader}
          />
        </div>
      </div>
      <CreateModal
        // IMPORTANT:
        // Do not pass a new `{}` each render. `CreateModal` syncs the `client` prop into
        // its internal form via an effect, and a new object reference would overwrite
        // the user's selected client (appearing to "revert to no client").
        showFullForm={true}
        modalOpen={addModalVisible}
        setModalOpen={setAddModalVisible}
        disableNavigation={true}
        onSuccess={() => {
          refetchUnassigned();
          refetchAssigned?.();
        }}
      />
      <AssignResourcesModal
        isOpen={isAssignModalOpen}
        onClose={handleCloseAssignModal}
        onAssign={handleAssignResources}
        job={selectedJob}
        isLoading={isAssigning}
      />
      <LegCreateModal
        visible={legModalVisible}
        setVisible={setLegModalVisible}
        form={legForm}
        job={legForm.form?.job || {}}
        onCancel={handleCloseLegModal}
      />
    </DndContext>
  );
};

export default DispatchLayout;
